export const TICKET_WIDTH = 42;

export type TextAlign = 'left' | 'center' | 'right';

export const padText = (
  text: string,
  width: number,
  align: TextAlign = 'left'
): string => {
  const truncated = text.length > width ? text.substring(0, width) : text;
  const padding = width - truncated.length;

  switch (align) {
    case 'center':
      const leftPad = Math.floor(padding / 2);
      const rightPad = padding - leftPad;
      return ' '.repeat(leftPad) + truncated + ' '.repeat(rightPad);
    case 'right':
      return ' '.repeat(padding) + truncated;
    default:
      return truncated + ' '.repeat(padding);
  }
};

export const formatLine = (
  leftText: string,
  rightText: string,
  width: number = TICKET_WIDTH
): string => {
  const minSpace = 2;
  const maxLeftWidth = width - rightText.length - minSpace;
  const truncatedLeft =
    leftText.length > maxLeftWidth
      ? leftText.substring(0, maxLeftWidth - 2) + '..'
      : leftText;
  const space = width - truncatedLeft.length - rightText.length;
  return truncatedLeft + ' '.repeat(space) + rightText;
};

export const centerText = (text: string, width: number = TICKET_WIDTH): string => {
  return padText(text, width, 'center');
};

export const separatorLine = (
  char: string = '-',
  width: number = TICKET_WIDTH
): string => {
  return char.repeat(width);
};

export const doubleSeparator = (width: number = TICKET_WIDTH): string => {
  return '='.repeat(width);
};

export const formatCategoryHeader = (
  categoryName: string,
  width: number = TICKET_WIDTH
): string => {
  const maxNameWidth = width - 4;
  const name = categoryName.toUpperCase();
  const truncatedName =
    name.length > maxNameWidth ? name.substring(0, maxNameWidth) : name;
  const sideWidth = Math.floor((width - truncatedName.length - 2) / 2);
  const leftSide = '='.repeat(sideWidth);
  const rightSide = '='.repeat(width - sideWidth - truncatedName.length - 2);
  return `${leftSide} ${truncatedName} ${rightSide}`;
};

export const formatPrice = (price: number, currency: string = 'EUR'): string => {
  return `${price.toFixed(2)}${currency}`;
};

export const formatQuantityItem = (
  quantity: number,
  name: string,
  price: number,
  currency: string = 'EUR',
  width: number = TICKET_WIDTH
): string => {
  const qtyStr = `${quantity}x `;
  const priceStr = formatPrice(price, currency);
  const minSpace = 2;
  const availableWidth = width - qtyStr.length - priceStr.length - minSpace;
  const truncatedName =
    name.length > availableWidth
      ? name.substring(0, availableWidth - 2) + '..'
      : name;
  const spaces = width - qtyStr.length - truncatedName.length - priceStr.length;
  return qtyStr + truncatedName + ' '.repeat(Math.max(minSpace, spaces)) + priceStr;
};

export const formatCustomization = (
  name: string,
  price: number | null,
  currency: string = 'EUR',
  width: number = TICKET_WIDTH,
  quantity: number = 1
): string => {
  const prefix = '   + ';
  const qtyStr = quantity > 1 ? ` x${quantity}` : '';
  const displayName = name + qtyStr;

  if (price && price > 0) {
    const totalPrice = price * quantity;
    const priceStr = `+${totalPrice.toFixed(2)}${currency}`;
    const minSpace = 2;
    const availableWidth = width - prefix.length - priceStr.length - minSpace;
    const truncatedName =
      displayName.length > availableWidth
        ? displayName.substring(0, availableWidth - 2) + '..'
        : displayName;
    const spaces = width - prefix.length - truncatedName.length - priceStr.length;
    return prefix + truncatedName + ' '.repeat(Math.max(minSpace, spaces)) + priceStr;
  }
  return prefix + displayName;
};

export const formatItemNote = (
  note: string,
  width: number = TICKET_WIDTH
): string[] => {
  const lines: string[] = [];
  lines.push(separatorLine('.', width));
  lines.push('\x1B\x21\x08');

  const prefix = '  NOTE: ';
  const maxWidth = width - prefix.length;

  const words = note.split(' ');
  let currentLine = '';

  for (const word of words) {
    if ((currentLine + ' ' + word).trim().length <= maxWidth) {
      currentLine = currentLine ? currentLine + ' ' + word : word;
    } else {
      if (currentLine) {
        lines.push(prefix + currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(prefix + currentLine);
  }

  lines.push('\x1B\x21\x00');
  lines.push(separatorLine('.', width));

  return lines;
};

export interface AggregatedOption {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export const aggregateCustomizationOptions = (
  options: Array<{ id: string; name: string; price: number }>
): AggregatedOption[] => {
  const optionMap = new Map<string, AggregatedOption>();

  for (const opt of options) {
    const key = `${opt.id}-${opt.name}-${opt.price}`;
    if (optionMap.has(key)) {
      const existing = optionMap.get(key)!;
      existing.quantity += 1;
    } else {
      optionMap.set(key, {
        id: opt.id,
        name: opt.name,
        price: opt.price,
        quantity: 1,
      });
    }
  }

  return Array.from(optionMap.values());
};

export const formatTotalLine = (
  label: string,
  value: string,
  width: number = TICKET_WIDTH,
  bold: boolean = false
): string => {
  const spaces = Math.max(1, width - label.length - value.length);
  const line = label + ' '.repeat(spaces) + value;
  if (bold) {
    return '\x1B\x21\x08\n' + line + '\n\x1B\x21\x00';
  }
  return line;
};

export interface TicketSection {
  type: 'header' | 'category' | 'items' | 'totals' | 'footer';
  categoryId?: string;
  categoryName?: string;
  content: string[];
}

export const wrapWithMarker = (
  content: string[],
  markerType: string,
  id?: string,
  name?: string
): string[] => {
  if (markerType === 'CAT' && id && name) {
    const safeName = name.replace(/[|\]:]/g, '_');
    return [
      `[CAT:${id}|${safeName}:START]`,
      ...content,
      `[CAT:${id}|${safeName}:END]`,
    ];
  }
  const markerId = id ? `:${id}` : '';
  return [
    `[${markerType}${markerId}:START]`,
    ...content,
    `[${markerType}${markerId}:END]`,
  ];
};

export const buildTicketWithMarkers = (sections: TicketSection[]): string => {
  const lines: string[] = [];

  for (const section of sections) {
    switch (section.type) {
      case 'header':
        lines.push(...wrapWithMarker(section.content, 'HEADER'));
        break;
      case 'category':
        lines.push(
          ...wrapWithMarker(section.content, 'CAT', section.categoryId, section.categoryName)
        );
        break;
      case 'items':
        lines.push(...wrapWithMarker(section.content, 'ITEMS'));
        break;
      case 'totals':
        lines.push(...wrapWithMarker(section.content, 'TOTALS'));
        break;
      case 'footer':
        lines.push(...wrapWithMarker(section.content, 'FOOTER'));
        break;
    }
  }

  return lines.join('\n');
};
